
export const length = (x: number, y: number) : number => Math.sqrt(x*x+y*y);
export const distanceBetween = (x1: number, y1: number, x2: number, y2: number) => length(x2-x1, y2-y1);

export const distanceToRect = (x:number, y:number, rect: DOMRect) : number => {
    if (x >= rect.left && x <= rect.right) {
        if (y < rect.top) {
            return rect.top - y;
        } else if (y > rect.bottom) {
            return y - rect.bottom;
        } else {
            return 0;
        }
    } else if (y >= rect.top && y <= rect.bottom) {
        if (x < rect.left) {
            return rect.left - x;
        } else if (x > rect.right) {
            return x - rect.right;
        } else {
            return 0;
        }
    } else if (x < rect.left) {
        const dx = rect.left - x;
        let dy;
        if (y < rect.top) {
            dy = rect.top - y;
        } else {
            dy = y - rect.bottom;
        }
        return length(dx, dy);
    } else /* x > rect.right */{
        const dx = x - rect.right;
        let dy;
        if (y < rect.top) {
            dy = rect.top - y;
        } else {
            dy = y - rect.bottom;
        }
        return length(dx, dy);
    }
};

export const distanceBetweenRects = (r1: DOMRect, r2: DOMRect): number => {
    const left = r2.right < r1.left;
    const right = r2.left > r1.right;
    const top = r2.bottom < r1.top;
    const bottom = r2.top > r1.bottom;
    if (left && top) {
        return distanceBetween(r2.right, r2.bottom, r1.left, r1.top);
    } else if (left && bottom) {
        return distanceBetween(r2.right, r2.top, r1.left, r1.bottom);
    } else if (right && top) {
        return distanceBetween(r2.left, r2.bottom, r1.right, r1.top);
    } else if (right && bottom) {
        return distanceBetween(r2.left, r2.top, r1.right, r1.bottom);
    } else if (left) {
        return r1.left - r2.right;
    } else if (right) {
        return r2.left - r1.right;
    } else if (top) {
        return r1.top - r2.bottom;
    } else if (bottom) {
        return r2.top - r1.bottom;
    } else {
        return 0;
    }
};

export const maxDistanceBetweenRects = (r1: DOMRect, r2: DOMRect): number => {
    const left = r2.right < r1.right;
    const right = r2.left > r1.left;
    const top = r2.bottom < r1.bottom;
    const bottom = r2.top > r1.top;
    if (left && top) {
        return distanceBetween(r2.left, r2.top, r1.right, r1.bottom);
    } else if (left && bottom) {
        return distanceBetween(r2.left, r2.bottom, r1.right, r1.top);
    } else if (right && top) {
        return distanceBetween(r2.right, r2.top, r1.left, r1.bottom);
    } else if (right && bottom) {
        return distanceBetween(r2.right, r2.bottom, r1.left, r1.top);
    } else if (left) {
        return r1.right - r2.left;
    } else if (right) {
        return r2.right - r1.left;
    } else if (top) {
        return r1.bottom - r2.top;
    } else if (bottom) {
        return r2.bottom - r1.top;
    } else {
        return 0; // Shouldn't happen!
    }
};

export const unionDomRects = (rects: DOMRect[]) => {
    if (rects.length === 0) { return undefined; }
    let {left, right, top, bottom} = rects[0];
    for (const rect of rects) {
        left = Math.min(left, rect.left);
        right = Math.max(right, rect.right);
        top = Math.min(top, rect.top);
        bottom = Math.max(bottom, rect.bottom);
    }
    return new DOMRect(left, top, right-left, bottom-top);
}

export const intersectRects = (a: DOMRect, b: DOMRect) => {
    if (a.left > b.right || b.left > a.right || a.top > b.bottom || b.top > a.bottom) {
        return null;
    } else {
        const left = Math.max(a.left, b.left);
        const top = Math.max(a.top, b.top);
        const right = Math.min(a.right, b.right);
        const bottom = Math.min(a.bottom, b.bottom);
        return new DOMRect(left, top, right-left, bottom-top);
    }
};

export const subtractRects = (a: DOMRect, b: DOMRect) => {
    const result: DOMRect[] = [];
    const intersection = intersectRects(a,b);
    if (!intersection) {
        return [a];
    }
    if (intersection.top > a.top) {
        result.push(new DOMRect(a.left, a.top, a.width, intersection.top - a.top));
    }
    const iTop = Math.max(a.top, intersection.top);
    const iBottom = Math.min(a.bottom, intersection.bottom);
    if (intersection.left > a.left) {
        result.push(new DOMRect(a.left, iTop, intersection.left - a.left, iBottom - iTop));
    }
    if (intersection.right < a.right) {
        result.push(new DOMRect(intersection.right, iTop, a.right - intersection.right, iBottom - iTop));
    }
    if (intersection.bottom < a.bottom) {
        result.push(new DOMRect(a.left, intersection.bottom, a.width, a.bottom - intersection.bottom));
    }
    return result;
}
